import { can, formatCode, WRITE_OFF_REASONS, writeOffStateProblem } from '@pl/domain';
import type { DiscrepancyDto, WriteOffReason } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { qk, useMe } from '../../lib/queries';
import { ErrorNote } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import s from './discrepancyKit.module.css';

/*
 * What the Office's Transfers and Counts pages share about a discrepancy: what a resolved one says,
 * and the lead's actions on an open one — shown disabled, marked "Lead only", to everyone else.
 */

type Resolve = { resolution: 'written_off'; reason: WriteOffReason; note?: string } | { resolution: 'left' } | { resolution: 'found' };

export const REASON_LABEL: Record<WriteOffReason, string> = { damaged: 'Damaged', lost: 'Lost', sample: 'Sample', other: 'Other' };

/** What a resolved discrepancy says, from the server's record: "Written off · Damaged", "Kept waiting". */
export function resolvedWords(d: DiscrepancyDto): string {
  switch (d.resolution) {
    case 'written_off':
      return d.writeOffReason ? `Written off · ${REASON_LABEL[d.writeOffReason]}` : 'Written off';
    case 'left':
      return d.source === 'transfer' ? 'Kept waiting' : 'Left for a recount';
    case 'found':
      return 'Found here';
    case 'received':
      return 'Received on its transfer';
    case 'shipped':
      // The server's note names the order: "Shipped on order PH-0003".
      return d.note ?? 'Shipped on its order';
    default:
      return 'Open';
  }
}

/** "5 h 52 min", "1 day": how long since an instant. */
export function sinceWords(iso: string, now: number = Date.now()): string {
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${min % 60} min`;
  const days = Math.floor(h / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Resolves one discrepancy. A resolve whose answer was lost, sent again, is refused with
 * ALREADY_RESOLVED: the first one landed. Its own count's or transfer's list says so, and if it was
 * resolved the way this lead asked, that is the answer. Otherwise the refusal stands.
 */
function useResolve(d: DiscrepancyDto) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Resolve) => {
      try {
        return await api.post<DiscrepancyDto>(`/discrepancies/${d.id}/resolve`, body);
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'ALREADY_RESOLVED') throw err;
        const list = await api.get<DiscrepancyDto[]>(`/discrepancies?sourceId=${d.sourceId}&limit=200`).catch(() => null);
        const now = list?.find((x) => x.id === d.id);
        if (now?.status !== 'resolved' || now.resolution !== body.resolution) throw err;
        return now;
      }
    },
    onSuccess: () => {
      // The row changes because the server says it did.
      void qc.invalidateQueries({ queryKey: ['discrepancies'] });
      if (d.source === 'count') void qc.invalidateQueries({ queryKey: qk.count(d.sourceId) });
      else {
        void qc.invalidateQueries({ queryKey: qk.transfer(d.sourceId) });
        void qc.invalidateQueries({ queryKey: ['transfers'] });
      }
    },
  });
}

/**
 * A lead's actions on one open discrepancy: a missing box is written off with a reason, or left —
 * for a recount, or on the road; an unexpected one is found here. Anyone else sees them disabled,
 * marked "Lead only": the server refuses them (`resolve_discrepancy`).
 */
export function ResolveActions({ d }: { d: DiscrepancyDto }) {
  const me = useMe();
  const lead = Boolean(me.data && can(me.data.role, 'resolve_discrepancy'));
  const resolve = useResolve(d);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<WriteOffReason | null>(null);
  const [note, setNote] = useState('');
  const name = useId();
  const noteId = useId();
  const code = formatCode(d.box.code);
  const off = !lead || resolve.isPending;
  const needNote = reason === 'other' && note.trim().length === 0;
  // Never offered where the server would refuse it, and why, as Box lookup says it. A picked box is
  // on an order, and is resolved from there.
  const noWriteOff = d.box.state === 'picked' ? 'Picked for an order — resolve from the order' : writeOffStateProblem(d.box.state);

  function close() {
    setOpen(false);
    setReason(null);
    setNote('');
    resolve.reset();
  }

  if (d.status !== 'open') return null;
  return (
    <div className={s.acts}>
      {d.kind === 'unexpected' ? (
        <button type="button" className="btn sm" disabled={off} onClick={() => resolve.mutate({ resolution: 'found' })}>
          <Icon name="check" />
          Found here
        </button>
      ) : (
        <>
          {noWriteOff ? (
            <span className={s.why}>{noWriteOff}</span>
          ) : (
            <button type="button" className="btn sm" disabled={off} onClick={() => setOpen(true)}>
              <Icon name="trash" />
              Write off
            </button>
          )}
          <button type="button" className="btn sm" disabled={off} onClick={() => resolve.mutate({ resolution: 'left' })}>
            <Icon name={d.kind === 'transit_missing' ? 'clock' : 'refresh'} />
            {d.kind === 'transit_missing' ? 'Keep waiting' : 'Leave for recount'}
          </button>
        </>
      )}
      {!lead && (
        <span className="lead">
          <Icon name="lock" />
          Lead only
        </span>
      )}
      {resolve.isError && !open && <ErrorNote error={resolve.error} />}

      <Modal
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        title={`Write off ${code}`}
        description="The box leaves stock for good. Say why."
        footer={
          <>
            <button type="button" className="btn" onClick={close}>
              Keep it
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={resolve.isPending || reason === null || needNote}
              onClick={() =>
                resolve.mutate(
                  { resolution: 'written_off', reason: reason!, ...(note.trim() ? { note: note.trim() } : {}) },
                  { onSuccess: close },
                )
              }
            >
              Write off box
            </button>
          </>
        }
      >
        {resolve.isError && <ErrorNote error={resolve.error} />}
        <fieldset className={s.reasons}>
          <legend className="sec-k">Reason</legend>
          {WRITE_OFF_REASONS.map((r) => (
            <label key={r} className={s.reason}>
              <input type="radio" name={name} value={r} checked={reason === r} onChange={() => setReason(r)} />
              {REASON_LABEL[r]}
            </label>
          ))}
        </fieldset>
        <div className="fld">
          <label htmlFor={noteId}>{reason === 'other' ? 'Note (needed for Other)' : 'Note'}</label>
          <textarea id={noteId} className="inp" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}
